'use client';

import { useState, type ReactNode } from 'react';
import { LifeBuoy } from 'lucide-react';
import { useLocale } from 'next-intl';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useMediaQuery } from '@/hooks/use-media-query';
import { usePostHogSupport } from '@/hooks/use-posthog-support';
import { getDocumentDirection } from '@/i18n/config';
import { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import type { UsePostHogSupportResult } from '@/lib/posthog-support/types';

import {
  PostHogSupportPanel,
  type PostHogSupportDraftController,
} from './posthog-support';
import type { SupportIntent } from './posthog-support-state';

type PostHogSupportLauncherProps = {
  accessibleLabel?: string;
  children?: ReactNode;
  className?: string;
  initialIntent?: SupportIntent;
};

export function PostHogSupportLauncher({
  ...props
}: PostHogSupportLauncherProps) {
  const { user, isLoading } = useAuth();
  const support = usePostHogSupport();
  const draftOwnerKey = isLoading ? 'loading' : (user?.userId ?? 'signed-out');
  return (
    <PostHogSupportLauncherForOwner
      key={draftOwnerKey}
      {...props}
      support={support}
    />
  );
}

function PostHogSupportLauncherForOwner({
  accessibleLabel,
  children,
  className,
  initialIntent,
  support,
}: PostHogSupportLauncherProps & { support: UsePostHogSupportResult }) {
  const locale = useLocale();
  const copy = getPostHogSupportCopy(locale);
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState<SupportIntent | null>(
    initialIntent ?? null
  );
  const [blockingWork, setBlockingWork] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [replyMessages, setReplyMessages] = useState<Record<string, string>>(
    {}
  );
  const unreadCount =
    support?.tickets.reduce((total, ticket) => total + ticket.unreadCount, 0) ??
    0;
  const baseTriggerLabel =
    accessibleLabel?.trim() ||
    (typeof children === 'string' && children.trim()
      ? children.trim()
      : copy.title);
  const triggerLabel =
    unreadCount > 0
      ? `${baseTriggerLabel}, ${copy.unread}: ${unreadCount}`
      : baseTriggerLabel;
  const draftController: PostHogSupportDraftController = {
    blockingWork,
    intent,
    newMessage,
    replyMessages,
    setIntent,
    setBlockingWork,
    setNewMessage,
    setReplyMessage: (ticketId, message) => {
      setReplyMessages((current) => ({ ...current, [ticketId]: message }));
    },
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-feedback-button="true"
          aria-label={triggerLabel}
          className={cn(
            'relative',
            !children &&
              'border-border/35 bg-background text-foreground hover:bg-muted focus-visible:ring-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium shadow-sm outline-none focus-visible:ring-2',
            className
          )}
        >
          {children ?? (
            <>
              <LifeBuoy className="size-4" aria-hidden />
              {copy.title}
            </>
          )}
          {unreadCount > 0 ? (
            <span
              className="bg-primary text-primary-foreground absolute -end-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-semibold tabular-nums"
              aria-hidden="true"
              dir="ltr"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </button>
      </SheetTrigger>
      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-support-unread-announcement
      >
        {unreadCount > 0 ? `${copy.unread}: ${unreadCount}` : ''}
      </span>

      <SheetContent
        side={
          isDesktop
            ? getDocumentDirection(locale) === 'rtl'
              ? 'left'
              : 'right'
            : 'bottom'
        }
        closeButton
        closeButtonClassName="inline-flex size-11 items-center justify-center sm:size-8"
        className={cn(
          'flex min-h-0 flex-col gap-0 overflow-hidden p-0',
          isDesktop
            ? 'h-dvh w-full sm:max-w-md'
            : 'h-[min(92dvh,48rem)] w-full rounded-t-2xl'
        )}
      >
        <SheetHeader className="sr-only">
          <SheetDescription>{copy.description}</SheetDescription>
        </SheetHeader>
        <PostHogSupportPanel
          compact
          dialog
          initialIntent={intent ?? initialIntent}
          draftController={draftController}
          support={support}
        />
      </SheetContent>
    </Sheet>
  );
}
